import { DndContext, closestCenter } from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'
import { restrictToParentElement } from '@dnd-kit/modifiers'

import { Box, Button, Stack } from '@mui/material'
import { PlusIcon } from 'shared/icons'
import Grid from '@mui/material/Grid'

import { Loading } from 'components/controls/loading'
import { BlockCaption } from 'components/controls/blockCaption'

import { useRemoveSearchsGuideLine, useListSearchsGuideLine, useSortSearchsGuideLine } from 'interface/search/guideLine'

import { useSortableList } from '../container/useSortableList'
import { useSortableListEdit } from '../container/useSortableListEdit'
import { EdittableDataListRecord, StaticDataListRecord } from '../components/record'

import { EditSearchsGuideLineDialog } from './dialog/edit'
import { AddSearchsGuideLineDialog } from './dialog/add'
import { ErrorInfo } from 'components/controls/errorInfo'

const SearchsGuideLineCaption = "対象ガイドライン"

const gridRules = {
  size: 12
}

type EdittableSearchsGuideLineListBlockProps = {
  searchId: number
  openGuideLine: (guideLineId: number) => void
}
const EdittableSearchsGuideLineListBlock = ({
  searchId,
  //openGuideLine
}: EdittableSearchsGuideLineListBlockProps) => {
  const { loading, data, error } = useListSearchsGuideLine(searchId)
  const list = useSortableList({ list: data?.list ?? [] })
  const { sortableList } = list
  const [postState, post] = useSortSearchsGuideLine(searchId)
  const [removeState, remove] = useRemoveSearchsGuideLine(searchId)
  const {
		modal, closeModal,
		onAdd, onEditIt, onRemoveIt,
		onSubmitEdit, onSubmitAdd,
		onDragEnd
  } = useSortableListEdit({
    ...list,
    post: (data) => post(data?.map(r => r.searchsGuideLineId) ?? [])
  })
  return (
    <Stack direction="column" position="relative">
      <Stack direction="row" spacing={1} alignItems="flex-end" width="100%" >
        <Box flexGrow={1}><BlockCaption flexGrow={1} caption={SearchsGuideLineCaption}/></Box>
        <Button
          startIcon={<PlusIcon />}
          variant="contained"
          size="small"
          sx={{ width: 'auto' }}
          onClick={onAdd}
        >
          追加
        </Button>
      </Stack>
      {error && <ErrorInfo error={error}/>}
      {(loading || postState.posting || removeState.posting) && <Loading/>}
      {(postState.posting || removeState.posting) && <Loading />}

      {sortableList.length > 0 && (
        <Grid container flexGrow={1} spacing={1} marginTop={1}>
          <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToParentElement]}
            onDragEnd={onDragEnd}
          >
            <SortableContext items={sortableList}>
              {sortableList.map((guideLine, i) => (
                <EdittableDataListRecord
                  key={`guideLine-${i}`}
                  data={guideLine}
                  displayName={guideLine.guideLineName ?? 'No name'}
                  onEditIt={() => {
                    onEditIt(guideLine)
                  }}
                  onRemoveIt={() => {
                    remove(guideLine.searchsGuideLineId)
                    onRemoveIt(guideLine)
                  }}
                  {...gridRules}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Grid>
      )}

      { modal?.command === 'add' ? (
        <AddSearchsGuideLineDialog
          searchId={searchId}
          onSubmit={onSubmitAdd}
          onClose={closeModal}
        />
      ) : (modal?.command === 'edit' && modal.data) ? (
        <EditSearchsGuideLineDialog
          searchId={searchId}
          guideLine={modal.data}
          onSubmit={onSubmitEdit}
          onClose={closeModal}
        />
      ) : (
        <></>
      )}
    </Stack>
  )
}

type StaticSearchsGuideLineListBlockProps = {
  searchId: number
  openGuideLine: (guideLineId: number) => void
}
const StaticSearchsGuideLineListBlock = ({
  searchId,
  //openGuideLine
}: StaticSearchsGuideLineListBlockProps) => {
  const { loading, data, error } = useListSearchsGuideLine(searchId)
  if (loading || !data || data.list.length === 0) return <></>
  return (
    <Stack direction="column" position="relative">
      <BlockCaption caption={SearchsGuideLineCaption} />
      {error && <ErrorInfo error={error}/>}

      <Grid container flexGrow={1} spacing={1} marginTop={0.5}>
        {data.list.map((guideLine, i) => (
          <StaticDataListRecord
            key={`guideLine-${i}`}
            displayName={guideLine.guideLineName ?? 'No name'}
            {...gridRules}
          />
        ))}
      </Grid>
    </Stack>
  )
}

type SearchsGuideLineListBlockProps = {
  edittable: boolean
  searchId: number
  openGuideLine: (guideLineId: number) => void
}
export const SearchsGuideLineListBlock = ({
  edittable,
  searchId,
  openGuideLine
}: SearchsGuideLineListBlockProps) => edittable ? (
  <EdittableSearchsGuideLineListBlock
    searchId={searchId}
    openGuideLine={openGuideLine}
  />
) : (
  <StaticSearchsGuideLineListBlock
    searchId={searchId}
    openGuideLine={openGuideLine}
  />
)
